import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  getStructure, updateStructureNode, backfillStructure, deriveAisleZones,
  createStructureNode, reparentStructureNode, deleteStructureNode, getStructureExport,
  type StructureNode, type StructureNodePatch, type StructureNodeCreate,
} from "../../../api/rest";
import { validParentOptions } from "../../structureAuthoring";
import { bindingsToIc3Csv } from "../../structureExport";
import { downloadTextFile } from "../../inventoryReport";
import { useSiteId } from "../../../hooks/useSiteId";
import css from "../../WarehouseAdminPage.module.css";

const PURPOSES = ["rack_storage", "floor_storage", "staging", "transition", "machinery", "dock", "office", "other"] as const;
const RACK_TYPES = ["single_deep", "double_deep", "flow", "drive_in", "shelf"] as const;
const SIDES = ["left", "right", "single"] as const;
const KINDS = ["zone", "aisle", "rack", "level", "space"] as const;

/**
 * U+00A0, written as an escape ON PURPOSE.
 *
 * The "Mover para" picker indents its options by depth. Browsers collapse and
 * trim ordinary leading whitespace inside `<option>` text, so a U+0020 indent
 * renders every aisle, level and space flush-left and the hierarchy is lost —
 * a regression neither the type-checker nor a text-match test can see. Keeping
 * it as `\u00a0` behind a named constant means a reformat or a file move cannot
 * silently swap it back for a plain space.
 */
const NBSP = "\u00a0";

/** Flatten the nested tree to (node, depth) rows so the list indents by depth
 *  while a single map/keyed render stays simple. Fully expanded in v1 —
 *  collapse and virtualization are deferred (ADR 168). */
function flatten(roots: StructureNode[], depth = 0): { node: StructureNode; depth: number }[] {
  const out: { node: StructureNode; depth: number }[] = [];
  for (const n of roots) {
    out.push({ node: n, depth });
    if (n.children?.length) out.push(...flatten(n.children, depth + 1));
  }
  return out;
}

function SourceBadge({ source }: { source: string }) {
  const { t } = useTranslation();
  const cls = source === "manual" ? css.badgeManual : source === "imported" ? css.badgeImported : css.badgeDerived;
  // Textual, not colour-alone (twin-design-system): the word carries the state.
  return <span className={`${css.badge} ${cls}`}>{t(`structure.graph.source.${source}`, { defaultValue: source })}</span>;
}

/**
 * ADR 168: the editable structure graph. Renders the derived tree and lets an
 * operator curate a node's fields; any edit promotes the node to `manual`
 * server-side so re-derivation preserves it. "Refresh from addresses" re-runs the
 * backfill (derived nodes rebuilt, manual ones kept).
 */
export function GraphSection() {
  const { t } = useTranslation();
  const siteId = useSiteId();
  const { warehouseId = "" } = useParams();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // ADR 188: optional address-grammar regex passed with the backfill — stored on the
  // warehouse's maps and used to re-derive aisle labels + levels (dash-less grammars).
  const [aislePattern, setAislePattern] = useState("");

  const { data: roots = [], isError } = useQuery({
    queryKey: ["structure", siteId, warehouseId],
    queryFn: () => getStructure(siteId, warehouseId),
    retry: 0,
    enabled: !!warehouseId,
  });

  const backfill = useMutation({
    mutationFn: () => backfillStructure(siteId, warehouseId, aislePattern.trim() || undefined),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["structure", siteId, warehouseId] }),
  });

  // ADR 213: one real zone per aisle, hulled from the member locations' footprints —
  // it lands in the zones table (and the ADR 143 zones layer) as kind "aisle".
  const aisleZones = useMutation({
    mutationFn: () => deriveAisleZones(siteId, warehouseId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["zones", siteId, warehouseId] }),
  });

  // S9 (ADR 174): the twin → iC3 structure export — the iC3-shaped binding CSV and the full graph JSON.
  const exportCsv = useMutation({
    mutationFn: () => getStructureExport(siteId, warehouseId),
    onSuccess: (data) => downloadTextFile(`structure-${warehouseId}.csv`, bindingsToIc3Csv(data.bindings), "text/csv"),
  });
  const exportJson = useMutation({
    mutationFn: () => getStructureExport(siteId, warehouseId),
    onSuccess: (data) => downloadTextFile(`structure-${warehouseId}.json`, JSON.stringify(data, null, 2), "application/json"),
  });

  const rows = useMemo(() => flatten(roots), [roots]);
  const selected = rows.find((r) => r.node.id === selectedId)?.node ?? null;

  return (
    <>
      <p className={css.note}>{t("structure.graph.note")}</p>

      <div className={css.graphActions}>
        <input className={css.input} value={aislePattern} onChange={(e) => setAislePattern(e.target.value)}
               placeholder={t("structure.graph.patternPlaceholder")} />
        <button className={css.linkBtn} type="button" disabled={backfill.isPending} onClick={() => backfill.mutate()}>
          {backfill.isPending ? t("structure.graph.refreshing") : t("structure.graph.refresh")}
        </button>
        {backfill.isSuccess && <span className={css.progress}>
          {backfill.data.relabeled > 0 || backfill.data.leveled > 0
            ? t("structure.graph.refreshedHealed", { nodes: backfill.data.nodes, relabeled: backfill.data.relabeled, leveled: backfill.data.leveled })
            : t("structure.graph.refreshed", { nodes: backfill.data.nodes })}
        </span>}
        {backfill.isError && <span className={css.resultError}>{backfill.error instanceof Error ? backfill.error.message : String(backfill.error)}</span>}
        <button className={css.linkBtn} type="button" disabled={aisleZones.isPending} onClick={() => aisleZones.mutate()}>
          {aisleZones.isPending ? t("structure.graph.aisleZonesPending") : t("structure.graph.aisleZones")}
        </button>
        {aisleZones.isSuccess && <span className={css.progress}>
          {t("structure.graph.aisleZonesDone", {
            aisles: aisleZones.data.aisles, created: aisleZones.data.created,
            updated: aisleZones.data.updated, deleted: aisleZones.data.deleted,
          })}
        </span>}
        {aisleZones.isError && <span className={css.resultError}>{t("structure.graph.aisleZonesError")}</span>}
        <button className={css.linkBtn} type="button" disabled={exportCsv.isPending} onClick={() => exportCsv.mutate()}>
          {t("structure.graph.exportCsv")}
        </button>
        <button className={css.linkBtn} type="button" disabled={exportJson.isPending} onClick={() => exportJson.mutate()}>
          {t("structure.graph.exportJson")}
        </button>
        {(exportCsv.isError || exportJson.isError) && <span className={css.resultError}>{t("structure.graph.exportError")}</span>}
      </div>

      <div className={css.graphLayout}>
        <div className={css.tree}>
          {rows.length === 0
            ? <p className={css.note}>{isError ? t("structure.graph.error") : t("structure.graph.empty")}</p>
            : rows.map(({ node, depth }) => (
              <button
                key={node.id}
                type="button"
                className={node.id === selectedId ? `${css.nodeRow} ${css.nodeSelected}` : css.nodeRow}
                style={{ paddingLeft: `${8 + depth * 18}px` }}
                onClick={() => setSelectedId(node.id)}
              >
                <span className={css.kindTag}>{t(`structure.graph.kind.${node.kind}`, { defaultValue: node.kind })}</span>
                <span className={css.nodeCode}>{node.name || node.code || node.path}</span>
                {node.name && node.code && <span className={css.nodeSub}>{node.code}</span>}
                <SourceBadge source={node.source} />
                {node.locationCount > 0 && <span className={css.count}>{t("structure.graph.locations", { n: node.locationCount })}</span>}
              </button>
            ))}
        </div>

        <div className={css.editor}>
          {selected
            ? <StructureNodeEditor key={selected.id} siteId={siteId} warehouseId={warehouseId}
                node={selected} rows={rows} onDeleted={() => setSelectedId(null)} />
            : <p className={css.note}>{t("structure.graph.select")}</p>}
          <StructureAddNode siteId={siteId} warehouseId={warehouseId} parent={selected}
            onCreated={(id) => setSelectedId(id)} />
        </div>
      </div>
    </>
  );
}

/** Editor for one node. Remounted (via `key={node.id}` at the call site) when the
 *  selection changes, so the form seeds cleanly from the newly selected node. */
function StructureNodeEditor({ siteId, warehouseId, node, rows, onDeleted }: {
  siteId: string; warehouseId: string; node: StructureNode;
  rows: { node: StructureNode; depth: number }[]; onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(node.name ?? "");
  const [purpose, setPurpose] = useState(node.purpose ?? "");
  const [rackType, setRackType] = useState(node.rackType ?? "");
  const [side, setSide] = useState(node.side ?? "");
  const [elevation, setElevation] = useState(node.elevationM != null ? String(node.elevationM) : "");
  const [clearHeight, setClearHeight] = useState(node.clearHeightM != null ? String(node.clearHeightM) : "");
  const [storable, setStorable] = useState(node.storable);
  const [occupiable, setOccupiable] = useState(node.occupiable);

  const showRack = node.kind === "aisle" || node.kind === "rack";
  const showHeights = node.kind === "level";

  function buildPatch(): StructureNodePatch {
    const p: StructureNodePatch = { name: name.trim(), purpose: purpose || null, storable, occupiable };
    if (showRack) { p.rackType = rackType || null; p.side = side || null; }
    if (showHeights) {
      p.elevationM = elevation.trim() === "" ? null : Number(elevation.replace(",", "."));
      p.clearHeightM = clearHeight.trim() === "" ? null : Number(clearHeight.replace(",", "."));
    }
    return p;
  }

  const save = useMutation({
    mutationFn: () => updateStructureNode(siteId, warehouseId, node.id, buildPatch()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["structure", siteId, warehouseId] }),
  });

  // S8 (ADR 173): re-parent (move / rack-assign) and delete, on top of the field edit.
  const parentOptions = useMemo(() => validParentOptions(rows, node.id), [rows, node.id]);
  const [moveTo, setMoveTo] = useState("");
  const [confirmDel, setConfirmDel] = useState(false);
  const move = useMutation({
    mutationFn: () => reparentStructureNode(siteId, warehouseId, node.id, moveTo === "__root__" ? null : moveTo),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["structure", siteId, warehouseId] }),
  });
  const del = useMutation({
    mutationFn: () => deleteStructureNode(siteId, warehouseId, node.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ["structure", siteId, warehouseId] }); onDeleted(); },
  });

  return (
    <form className={css.editorForm} onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <div className={css.editorHeader}>
        <span>{t("structure.graph.editing", {
          kind: t(`structure.graph.kind.${node.kind}`, { defaultValue: node.kind }), code: node.code ?? node.path,
        })}</span>
        <SourceBadge source={node.source} />
      </div>

      <div className={css.field}>
        <label className={css.label} htmlFor="sg-name">{t("structure.graph.field.name")}</label>
        <input id="sg-name" className={css.input} value={name} onChange={(e) => setName(e.target.value)}
          placeholder={node.code ?? ""} />
      </div>

      <div className={css.field}>
        <label className={css.label} htmlFor="sg-purpose">{t("structure.graph.field.purpose")}</label>
        <select id="sg-purpose" className={css.input} value={purpose} onChange={(e) => setPurpose(e.target.value)}>
          <option value="">{t("structure.graph.unset")}</option>
          {PURPOSES.map((p) => <option key={p} value={p}>{t(`structure.graph.purpose.${p}`)}</option>)}
        </select>
      </div>

      {showRack && (
        <>
          <div className={css.field}>
            <label className={css.label} htmlFor="sg-racktype">{t("structure.graph.field.rackType")}</label>
            <select id="sg-racktype" className={css.input} value={rackType} onChange={(e) => setRackType(e.target.value)}>
              <option value="">{t("structure.graph.unset")}</option>
              {RACK_TYPES.map((r) => <option key={r} value={r}>{t(`structure.graph.rackType.${r}`)}</option>)}
            </select>
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="sg-side">{t("structure.graph.field.side")}</label>
            <select id="sg-side" className={css.input} value={side} onChange={(e) => setSide(e.target.value)}>
              <option value="">{t("structure.graph.unset")}</option>
              {SIDES.map((s) => <option key={s} value={s}>{t(`structure.graph.side.${s}`)}</option>)}
            </select>
          </div>
        </>
      )}

      {showHeights && (
        <>
          <div className={css.field}>
            <label className={css.label} htmlFor="sg-elev">{t("structure.graph.field.elevation")}</label>
            <input id="sg-elev" className={css.input} inputMode="decimal" value={elevation} onChange={(e) => setElevation(e.target.value)} />
          </div>
          <div className={css.field}>
            <label className={css.label} htmlFor="sg-clear">{t("structure.graph.field.clearHeight")}</label>
            <input id="sg-clear" className={css.input} inputMode="decimal" value={clearHeight} onChange={(e) => setClearHeight(e.target.value)} />
          </div>
        </>
      )}

      <label className={css.checkRow}>
        <input type="checkbox" checked={storable} onChange={(e) => setStorable(e.target.checked)} />
        {t("structure.graph.field.storable")}
      </label>
      <label className={css.checkRow}>
        <input type="checkbox" checked={occupiable} onChange={(e) => setOccupiable(e.target.checked)} />
        {t("structure.graph.field.occupiable")}
      </label>

      <div>
        <button className={css.btn} type="submit" disabled={save.isPending}>
          {save.isPending ? t("structure.graph.saving") : t("structure.graph.save")}
        </button>
      </div>
      {save.isError && <p className={css.resultError} role="alert">{save.error instanceof Error ? save.error.message : String(save.error)}</p>}
      {save.isSuccess && <p className={css.resultOk} role="status">{t("structure.graph.saved")}</p>}

      <div className={css.field}>
        <label className={css.label} htmlFor="sg-move">{t("structure.graph.moveTo")}</label>
        <div className={css.inlineRow}>
          <select id="sg-move" className={css.input} value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
            <option value="">{t("structure.graph.chooseParent")}</option>
            <option value="__root__">{t("structure.graph.rootLevel")}</option>
            {/* NBSP, never a plain space — see the constant's note. */}
            {parentOptions.map((o) => (
              <option key={o.id} value={o.id}>{`${NBSP.repeat(o.depth * 2)}${o.label}`}</option>
            ))}
          </select>
          <button className={css.linkBtn} type="button" disabled={!moveTo || move.isPending} onClick={() => move.mutate()}>
            {t("structure.graph.move")}
          </button>
        </div>
        {move.isError && <p className={css.resultError} role="alert">{move.error instanceof Error ? move.error.message : String(move.error)}</p>}
      </div>

      {node.source === "manual" && (
        <div className={css.inlineRow}>
          {confirmDel ? (
            <>
              <span className={css.note}>{t("structure.graph.deleteConfirm")}</span>
              <button className={css.dangerBtn} type="button" disabled={del.isPending} onClick={() => del.mutate()}>
                {t("structure.graph.deleteYes")}
              </button>
              <button className={css.linkBtn} type="button" onClick={() => setConfirmDel(false)}>
                {t("structure.graph.cancel")}
              </button>
            </>
          ) : (
            <button className={css.dangerBtn} type="button" onClick={() => setConfirmDel(true)}>
              {t("structure.graph.delete")}
            </button>
          )}
          {del.isError && <p className={css.resultError} role="alert">{del.error instanceof Error ? del.error.message : String(del.error)}</p>}
        </div>
      )}
    </form>
  );
}

/** ADR 173, slice S8: author a new manual node — a rack, a dock, an office — under the selected
 *  node (or at the root when nothing is selected). The grammar can't derive these; the operator
 *  models them here and they survive every backfill (source='manual'). */
function StructureAddNode({ siteId, warehouseId, parent, onCreated }: {
  siteId: string; warehouseId: string; parent: StructureNode | null; onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [kind, setKind] = useState<(typeof KINDS)[number]>("rack");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  const create = useMutation({
    mutationFn: () => {
      const req: StructureNodeCreate = {
        parentId: parent?.id ?? null, kind, code: code.trim(),
        name: name.trim() || null, purpose: null,
      };
      return createStructureNode(siteId, warehouseId, req);
    },
    onSuccess: (n) => {
      void qc.invalidateQueries({ queryKey: ["structure", siteId, warehouseId] });
      setCode(""); setName("");
      onCreated(n.id);
    },
  });

  return (
    <form className={css.editorForm} onSubmit={(e) => { e.preventDefault(); if (code.trim()) create.mutate(); }}>
      <div className={css.editorHeader}>
        <span>{parent
          ? t("structure.graph.addUnder", { name: parent.name || parent.code || parent.path })
          : t("structure.graph.addRoot")}</span>
      </div>
      <div className={css.field}>
        <label className={css.label} htmlFor="sg-add-kind">{t("structure.graph.field.kind")}</label>
        <select id="sg-add-kind" className={css.input} value={kind}
          onChange={(e) => setKind(e.target.value as (typeof KINDS)[number])}>
          {KINDS.map((k) => <option key={k} value={k}>{t(`structure.graph.kind.${k}`, { defaultValue: k })}</option>)}
        </select>
      </div>
      <div className={css.field}>
        <label className={css.label} htmlFor="sg-add-code">{t("structure.graph.field.code")}</label>
        <input id="sg-add-code" className={css.input} value={code} onChange={(e) => setCode(e.target.value)} />
      </div>
      <div className={css.field}>
        <label className={css.label} htmlFor="sg-add-name">{t("structure.graph.newName")}</label>
        <input id="sg-add-name" className={css.input} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <button className={css.btn} type="submit" disabled={create.isPending || !code.trim()}>
          {create.isPending ? t("structure.graph.adding") : t("structure.graph.add")}
        </button>
      </div>
      {create.isError && <p className={css.resultError} role="alert">{create.error instanceof Error ? create.error.message : String(create.error)}</p>}
    </form>
  );
}
